import { useEffect, useMemo, useState } from 'react';
import { payrollTaxesGateway } from '../../payroll-taxes';
import { taxFormCodeLabels } from '../../payroll-taxes/types';
import type { TaxDepositResponse, TaxLiabilityResponse } from '../../payroll-taxes/types';
import { formatReportCurrency } from '../../reports/formatting';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { downloadCsv } from '../csvExport';
import '../accounting-reports.css';

/** Real, federal-only liability types this platform's tax engine actually produces today (see
 * `MockTaxCalculationProvider`/`TaxFormLineMappings`) — never invented categories for State/Local/SUI/
 * Garnishments/Retirement/Benefits, which no module in EPN computes as a liability yet. */
function liabilityTypeLabel(formCode: number): string {
  return formCode === 1 ? 'Federal Income Tax Withholding + FICA' : formCode === 2 ? 'Federal Unemployment Tax (FUTA)' : taxFormCodeLabels[formCode] ?? 'Unknown';
}

function depositStatusLabel(deposit: TaxDepositResponse | undefined): { text: string; className: string } {
  if (!deposit) return { text: 'Not Yet Deposited', className: 'badge badge--warning' };
  if (deposit.status === 'Settled') return { text: 'Settled', className: 'badge badge--success' };
  if (deposit.status === 'Failed') return { text: 'Failed', className: 'badge badge--blocked' };
  return { text: deposit.status, className: 'badge badge--warning' };
}

/** EPN-77 Payroll Liabilities report. Reads real `TaxLiabilityResponse`/`TaxDepositResponse` history via
 * the existing `payrollTaxesGateway` (EPN.TaxCompliance.Api) — no new backend, no new data invented.
 * EPN's tax engine only models 2 federal forms (Form 941, Form 940) today: there is no State Income Tax,
 * Local Tax, SUI, Garnishment, Retirement, or Benefits liability anywhere in the platform, and TaxDeposit
 * has no partial-payment/remaining-amount concept and no Agency/Payee, Jurisdiction, or Due Date field.
 * Every one of those is rendered as "Not tracked" per the SRS's own instruction, never guessed. */
export function PayrollLiabilitiesReportPage() {
  const [liabilities, setLiabilities] = useState<TaxLiabilityResponse[]>([]);
  const [deposits, setDeposits] = useState<Record<string, TaxDepositResponse[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<number | 'all'>('all');

  useEffect(() => {
    const controller = new AbortController();
    payrollTaxesGateway
      .listLiabilities(controller.signal)
      .then(async (result) => {
        if (controller.signal.aborted) return;
        setLiabilities(result);
        const depositEntries = await Promise.all(
          result.map(async (liability) => [liability.id, await payrollTaxesGateway.listDeposits(liability.id, controller.signal)] as const),
        );
        if (!controller.signal.aborted) setDeposits(Object.fromEntries(depositEntries));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(friendlyErrorMessage(err, 'Could not load payroll liabilities.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const years = useMemo(() => [...new Set(liabilities.map((l) => l.taxYear))].sort((a, b) => b - a), [liabilities]);
  const filtered = yearFilter === 'all' ? liabilities : liabilities.filter((l) => l.taxYear === yearFilter);

  const chips = yearFilter === 'all' ? [] : [{ key: 'year', label: `Tax year: ${yearFilter}`, onRemove: () => setYearFilter('all') }];

  function exportCsv() {
    downloadCsv(
      'payroll-liabilities.csv',
      ['Liability Type', 'Pay Date', 'Amount Generated', 'Amount Paid', 'Status'],
      filtered.map((liability) => {
        const latestDeposit = deposits[liability.id]?.[0];
        const status = depositStatusLabel(latestDeposit);
        const amountPaid = latestDeposit?.status === 'Settled' ? liability.totalAmount : null;
        return [liabilityTypeLabel(liability.formCode), 'Not tracked', liability.totalAmount.toFixed(2), amountPaid !== null ? amountPaid.toFixed(2) : 'Not tracked', status.text];
      }),
    );
  }

  return (
    <section className="card card--wide" aria-labelledby="payroll-liabilities-heading">
      <div className="section-header">
        <h2 id="payroll-liabilities-heading">Payroll Liabilities</h2>
        <button type="button" onClick={exportCsv} disabled={filtered.length === 0}>Export CSV</button>
      </div>
      <p className="muted">
        Payroll liabilities are calculated tax obligations, not the same thing as deposit/payment status — see the Status
        column for what Escochex can actually confirm about each one.
      </p>

      {loading && <p className="muted" role="status">Loading payroll liabilities…</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {!loading && !error && liabilities.length === 0 && (
        <p className="report-empty-state">No payroll liabilities have been calculated yet.</p>
      )}

      {!loading && years.length > 1 && (
        <div className="report-filter-bar">
          <div className="report-filter-field">
            <label htmlFor="liabilities-year">Tax year</label>
            <select id="liabilities-year" value={yearFilter} onChange={(e) => setYearFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
              <option value="all">All years</option>
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <ActiveFilterChips chips={chips} onClearAll={() => setYearFilter('all')} />

      {!loading && filtered.length > 0 && (
        <table aria-label="Payroll liabilities">
          <thead>
            <tr>
              <th scope="col">Liability Type</th>
              <th scope="col">Agency / Payee</th>
              <th scope="col">Jurisdiction</th>
              <th scope="col">Pay Date</th>
              <th scope="col">Amount Generated</th>
              <th scope="col">Amount Paid</th>
              <th scope="col">Remaining Amount</th>
              <th scope="col">Due Date</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((liability) => {
              const latestDeposit = deposits[liability.id]?.[0];
              const status = depositStatusLabel(latestDeposit);
              const amountPaid = latestDeposit?.status === 'Settled' ? liability.totalAmount : null;
              return (
                <tr key={liability.id}>
                  <th scope="row">{liabilityTypeLabel(liability.formCode)}</th>
                  <td><span className="report-not-tracked">Not tracked</span></td>
                  <td><span className="report-not-tracked">Not tracked</span></td>
                  <td><span className="report-not-tracked">Not tracked</span></td>
                  <td>{formatReportCurrency(liability.totalAmount)}</td>
                  <td>{amountPaid !== null ? formatReportCurrency(amountPaid) : <span className="report-not-tracked">Not tracked</span>}</td>
                  <td>{amountPaid !== null ? formatReportCurrency(liability.totalAmount - amountPaid) : <span className="report-not-tracked">Not tracked</span>}</td>
                  <td><span className="report-not-tracked">Not tracked</span></td>
                  <td><span className={status.className}>{status.text}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p className="muted">
        Agency/Payee, Jurisdiction, Due Date, and partial-payment tracking are not yet modeled anywhere in EPN's tax
        engine — shown as "Not tracked" rather than guessed. Only Form 941 (federal income tax withholding + FICA)
        and Form 940 (FUTA) liabilities exist today; State, Local, SUI, Garnishment, Retirement, and Benefits
        liabilities are not yet computed by any module in this platform.
      </p>
    </section>
  );
}
